import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Rocket, Sparkles, GanttChartSquare, LayoutDashboard, IdCard, NotebookPen, ShieldCheck, ArrowRight, ArrowLeft, Orbit,
} from "lucide-react";

export const TOUR_STEPS = [
  {
    icon: Orbit,
    title: "Welcome to Project Management Orchestrator",
    tagline: "One plan. Endless outcomes.",
    points: [
      "Build project plans in minutes — by hand, from templates, or with AI",
      "Visualize them as WBS, Gantt, Visual maps, Boards and executive Roadmaps",
      "Let the tool track progress, warn you early, and keep your team aligned",
    ],
  },
  {
    icon: Sparkles,
    title: "Build your plan",
    tagline: "Plan · Gantt · Visual · Roadmap",
    points: [
      "Click AI plan to generate a full task list with phases, dates and dependencies",
      "Edit inline in the Plan (WBS) tab — export or print it anytime",
      "Dependencies auto-propagate: slip one task and the schedule updates",
      "Set a Baseline so you always know what slipped and what improved",
    ],
  },
  {
    icon: Rocket,
    title: "Stay in control",
    tagline: "Dashboard · True-ups · Pilot Auto Pilot",
    points: [
      "Every project opens on a Dashboard: due items, risks, audit to-dos and quick-adds",
      "Turn on Pilot and the plan advances itself daily based on each person's speed",
      "Pilot caps at 99% — only YOU confirm 100% in a weekly true-up, which teaches it real velocity",
      "Get warned when tasks near completion or are forecast to finish late",
    ],
  },
  {
    icon: IdCard,
    title: "Keep everyone aligned",
    tagline: "Cards · Notebook · Artifacts",
    points: [
      "Cards give each person a snapshot of their open items — emailed on your cadence",
      "Magic links let teammates submit % complete without ever logging in",
      "Paste a meeting transcript in the Notebook and AI drafts minutes + finds action items",
      "Store charters, docs and recordings in Artifacts with version history",
    ],
  },
  {
    icon: LayoutDashboard,
    title: "See the big picture",
    tagline: "Portfolio · Audit · Status reports",
    points: [
      "The Portfolio rolls up health, risks and resource alerts across every project",
      "Audit scores keep your PM hygiene on track (baseline set, risks logged, artifacts filed)",
      "Publish status reports with public read-only links for stakeholders",
    ],
  },
];

export default function WelcomeTour({ open, onOpenChange, onFinish }) {
  const [step, setStep] = useState(0);
  const nav = useNavigate();
  useEffect(() => { if (open) setStep(0); }, [open]);

  const s = TOUR_STEPS[step];
  const last = step === TOUR_STEPS.length - 1;
  const close = () => { onOpenChange(false); onFinish?.(); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="welcome-tour">
        <div className="bg-primary text-primary-foreground px-6 py-5">
          <s.icon className="h-8 w-8 mb-3" />
          <h2 className="font-display text-2xl font-extrabold tracking-tight">{s.title}</h2>
          <p className="text-primary-foreground/80 text-sm mt-0.5">{s.tagline}</p>
        </div>
        <div className="px-6 py-5">
          <ul className="space-y-2.5">
            {s.points.map((p, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-between mt-6">
            <div className="flex gap-1.5" data-testid="welcome-dots">
              {TOUR_STEPS.map((_, i) => (
                <button key={i} onClick={() => setStep(i)} className={`h-2 rounded-full transition-all ${i === step ? "w-6 bg-primary" : "w-2 bg-border"}`} data-testid={`welcome-dot-${i}`} />
              ))}
            </div>
            <div className="flex gap-2">
              {step > 0 && <Button variant="ghost" size="sm" className="gap-1" onClick={() => setStep(step - 1)} data-testid="welcome-back"><ArrowLeft className="h-3.5 w-3.5" /> Back</Button>}
              {!last ? (
                <Button size="sm" className="gap-1" onClick={() => setStep(step + 1)} data-testid="welcome-next">Next <ArrowRight className="h-3.5 w-3.5" /></Button>
              ) : (
                <Button size="sm" className="gap-1" onClick={() => { close(); nav("/dashboard?new=1"); }} data-testid="welcome-cta">Create my first project <Rocket className="h-3.5 w-3.5" /></Button>
              )}
            </div>
          </div>
          <button onClick={close} className="text-xs text-muted-foreground hover:text-foreground mt-3 underline" data-testid="welcome-skip">
            Skip for now — you can replay this from the Guide anytime
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AutoWelcomeTour() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    api.get("/me/welcome").then(async ({ data }) => {
      if (data.seen) return;
      if (!user.tour_done) {
        // Brand-new users with no projects get the interactive FirstRunTour instead
        try {
          const { data: projects } = await api.get("/projects");
          if ((projects || []).length === 0) return;
        } catch (err) { console.debug("welcome tour project check failed", err); }
      }
      setOpen(true);
    }).catch(() => {});
  }, [user]);

  const markSeen = () => api.post("/me/welcome").catch(() => {});

  return <WelcomeTour open={open} onOpenChange={setOpen} onFinish={markSeen} />;
}
